import { useTranslation } from 'react-i18next';
import { DashboardCardWrapper } from '../../../components/DashboardCardWrapper/dashboard-card-wrapper';
import { DashboardCardContentValue } from '../../../components/DashboardCardContentValue/dashboard-card-content-value';
import { toCurrencyString, CurrencyCode } from '@client/toolkit';
import { BarChart } from '../../../components';
import { usePostRentalIncomesDashboardMutation } from '@client/api';
import { useEffect, useMemo, useCallback } from 'react';
import { NoDataWidget } from '../NoDataWidget/no-data-widget';
import { BarTooltipProps, BarDatum } from '@nivo/bar';
import dayjs from 'dayjs';
import 'dayjs/locale/nb';

interface TotalIncomeWidgetProps {
  buildingIds: string[];
  selectedDateFrom: string;
  selectedDateTo: string;
}

export const TotalIncomeWidget = ({ buildingIds, selectedDateFrom, selectedDateTo }: TotalIncomeWidgetProps) => {
  const { t, i18n } = useTranslation('widget');
  const [trigger, { data, isLoading }] = usePostRentalIncomesDashboardMutation();

  useEffect(() => {
    if (!selectedDateFrom || !selectedDateTo) return;
    trigger({
      startDate: selectedDateFrom,
      endDate: selectedDateTo,
      body: {
        buildingIds: buildingIds,
      },
    });
  }, [buildingIds, selectedDateFrom, selectedDateTo, trigger]);

  const now = dayjs();

  const orderedMonths = useMemo(() => {
    const startDate = selectedDateFrom ? dayjs(selectedDateFrom) : now;
    return Array.from({ length: 12 }, (_, i) => {
      const date = startDate.add(i, 'month');
      return { calendarMonth: date.month() + 1, year: date.year() };
    });
  }, [selectedDateFrom]);

  const isMultiYear = orderedMonths[0].year !== orderedMonths[11].year;

  const monthlyIncomesMemoized = useMemo(() => {
    const currentMonth = now.month() + 1; // 1-indexed
    const currentYear = now.year();
    const rangeStart = dayjs(selectedDateFrom);
    const rangeEnd = dayjs(selectedDateTo);
    const isEntirelyFuture = rangeStart.isAfter(now, 'day');
    const isEntirelyPast = rangeEnd.isBefore(now, 'day');

    const futureFlags = orderedMonths.map(({ calendarMonth, year }) => {
      if (isEntirelyFuture) return true;
      if (isEntirelyPast) return false;
      return year > currentYear || (year === currentYear && calendarMonth > currentMonth);
    });

    const monthlyIncomes = orderedMonths.map((_, slotIndex) => ({
      slot: slotIndex,
      income: 0,
      futureIncome: 0,
      commonAreaCosts: 0,
      futureCommonAreaCosts: 0,
    }));

    data?.monthlyIncomes?.forEach((monthlyIncome) => {
      const slotIndex = orderedMonths.findIndex((s) => s.calendarMonth === monthlyIncome.month);
      if (slotIndex >= 0) {
        if (futureFlags[slotIndex]) {
          monthlyIncomes[slotIndex].futureIncome = monthlyIncome.income.amount;
        } else {
          monthlyIncomes[slotIndex].income = monthlyIncome.income.amount;
        }
      }
    });

    data?.monthlyCommonAreaCosts?.forEach((monthlyCommonArea) => {
      const slotIndex = orderedMonths.findIndex((s) => s.calendarMonth === monthlyCommonArea.month);
      if (slotIndex >= 0) {
        if (futureFlags[slotIndex]) {
          monthlyIncomes[slotIndex].futureCommonAreaCosts = monthlyCommonArea.income.amount;
        } else {
          monthlyIncomes[slotIndex].commonAreaCosts = monthlyCommonArea.income.amount;
        }
      }
    });

    return monthlyIncomes;
  }, [data?.monthlyIncomes, data?.monthlyCommonAreaCosts, orderedMonths, selectedDateFrom, selectedDateTo, now]);

  const totalAmount = (data?.totalRentalIncome?.amount ?? 0) + (data?.totalCommonAreaCosts?.amount ?? 0);

  const currencyCode = data?.totalRentalIncome?.currency?.code as CurrencyCode;

  const dayjsLocale = i18n.language === 'no' ? 'nb' : 'en';

  const formatMonth = useCallback(
    (slot: number) => {
      const { calendarMonth, year } = orderedMonths[slot] ?? { calendarMonth: slot + 1, year: now.year() };
      const monthName = dayjs()
        .locale(dayjsLocale)
        .month(calendarMonth - 1)
        .format('MMM');
      if (!isMultiYear) return monthName;
      return `${monthName} '${String(year).slice(2)}`;
    },
    [dayjsLocale, isMultiYear, orderedMonths],
  );

  const CustomTooltip = useCallback(
    ({ id, value, color, indexValue }: BarTooltipProps<BarDatum>) => {
      const isRental = id === 'income' || id === 'futureIncome';
      const label = isRental ? t('totalIncome.rentalIncome') : t('totalIncome.commonAreaCosts');
      const baseColor = isRental ? '#3F98B4' : '#8DD4E8';
      return (
        <div className="bg-gray-800 text-white px-3 py-2 rounded shadow-lg text-sm">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: baseColor }} />
            <span className="font-medium">{label}:</span>
            <span>
              {toCurrencyString(value, undefined, {
                currency: currencyCode,
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}
            </span>
          </div>
        </div>
      );
    },
    [t, currencyCode],
  );

  // Only show NoDataWidget after loading is complete and there's no data
  if (!isLoading && data && totalAmount === 0)
    return <NoDataWidget title={t('totalIncome.title')} noDataText={t('totalIncome.noData')} />;

  return (
    <DashboardCardWrapper
      to="/reporting/rental-income"
      title={t('totalIncome.title')}
      tooltip={t('totalIncome.tooltip')}
      data-testid="total-income-widget"
      loading={!data || isLoading}
    >
      <DashboardCardContentValue
        value={toCurrencyString(totalAmount, undefined, {
          currency: data?.totalRentalIncome?.currency?.code as CurrencyCode,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}
      />
      <div className="w-full h-[200px]" data-testid="total-income-chart">
        <BarChart
          data={monthlyIncomesMemoized}
          indexBy="slot"
          keys={['income', 'futureIncome', 'commonAreaCosts', 'futureCommonAreaCosts']}
          colors={({ id }) => {
            switch (id) {
              case 'income':
                return '#3F98B4';
              case 'futureIncome':
                return '#3F98B488';
              case 'commonAreaCosts':
                return '#8DD4E8';
              case 'futureCommonAreaCosts':
                return '#6BC5DC88';
              default:
                return '#3F98B4';
            }
          }}
          groupMode="grouped"
          tooltip={CustomTooltip}
          axisBottom={{
            tickSize: 0,
            tickPadding: 8,
            tickRotation: -35,
            format: (v) => formatMonth(Number(v)),
          }}
          defs={[
            {
              id: 'future-lines',
              type: 'patternLines',
              spacing: 4,
              rotation: -45,
              lineWidth: 1,
              background: 'inherit',
              color: 'rgba(0, 0, 0, 0.3)',
            },
          ]}
          fill={[
            { match: { id: 'futureIncome' }, id: 'future-lines' },
            { match: { id: 'futureCommonAreaCosts' }, id: 'future-lines' },
          ]}
          legends={[
            {
              dataFrom: 'keys',
              anchor: 'bottom',
              direction: 'row',
              translateX: 0,
              translateY: 50,
              itemWidth: 120,
              itemHeight: 16,
              data: [
                {
                  color: '#3F98B4',
                  id: 'income',
                  label: t('totalIncome.rentalIncome'),
                },
                {
                  color: '#8DD4E8',
                  id: 'commonAreaCosts',
                  label: t('totalIncome.commonAreaCosts'),
                },
                {
                  color: '#3F98B488',
                  id: 'futureIncome',
                  label: t('totalIncome.futureIncome'),
                },
              ],
            },
          ]}
        />
      </div>
    </DashboardCardWrapper>
  );
};
